import React, { useState, useEffect, useMemo } from 'react'
import { Row, Col, Container, Button, Badge, Spinner, Tab, Nav, Modal, Form } from 'react-bootstrap'
import Card from '../../../components/Card'
import { Link, useNavigate } from 'react-router-dom'
import Moment from 'moment';
import { apiHandler } from '../../../api/apihandler';
import { CLASS_TRANSACTIONS, GRADE_TRANSACTIONS, GRADE_TRANSACTION_PAY } from "../../../api/constants";
import Swal from 'sweetalert2'

//profile-header
import ProfileHeader from '../../../components/profile-header'

import img6 from '../../../assets/images/page-img/profile-bg6.jpg'
import NoDataFound from '../../../components/noDataFound';

const MyTransactions = () => {
    const navigate = useNavigate();
    const [classTransactions, setClassTransactions] = useState([]);
    const [gradeTransactions, setGradeTransactions] = useState([]);
    const [showMoreDataLoader, setShowMoreDataLoader] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [showPayModal, setShowPayModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [savedCards, setSavedCards] = useState([]);
    const [selectedCardToken, setSelectedCardToken] = useState('');
    const [isPaying, setIsPaying] = useState(false);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    useEffect(() => {
        getGradeTransactionsApi()
        getClassTransactionsApi()
    }, []);

    const summary = useMemo(() => {
        const totalTransactions = gradeTransactions.length;
        const totalPaid = gradeTransactions.reduce((total, item) => total + Number(item?.total_amount || item?.price || 0), 0);
        const totalSubsidy = gradeTransactions.reduce((total, item) => total + Number(item?.subsidy_amount || 0), 0);
        const pendingCount = gradeTransactions.filter((item) => Number(item?.status) !== 1).length;
        return { totalTransactions, totalPaid, totalSubsidy, pendingCount };
    }, [gradeTransactions]);

    const getPaymentTypeLabel = (paymentType) => {
        if (Number(paymentType) === 0) return 'Weekly Payment';
        if (Number(paymentType) === 1) return 'Monthly Payment';
        return 'One Time Payment';
    };

    const getStatusMeta = (transaction) => {
        if (transaction?.transaction_id) {
            return { label: 'Paid', variant: 'success', icon: 'verified' };
        }
        if (Number(transaction?.status) === 1) {
            return { label: 'Completed', variant: 'success', icon: 'check_circle' };
        }
        return { label: 'Pending', variant: 'warning', icon: 'schedule' };
    };

    const isUnpaidTransaction = (transaction) => !transaction?.transaction_id && Number(transaction?.status) !== 1;
    const isSelectedPayRow = (transaction) => showPayModal && selectedTransaction?.id === transaction?.id;

    const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

    const getSavedQuickbookCards = () => {
        try {
            const cards = JSON.parse(localStorage.getItem('harmonydaycare_saved_cards') || '[]');
            return Array.isArray(cards) ? cards : [];
        } catch (error) {
            return [];
        }
    };

    const openPayModal = (transaction) => {
        const cards = getSavedQuickbookCards();
        if (!cards.length) {
            Swal.fire({
                title: 'No Saved Card',
                text: 'Please add a card first to pay pending transactions.',
                icon: 'info',
                confirmButtonText: 'Go to Saved Cards'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/dashboards/app/saved-card-list');
                }
            });
            return;
        }

        setSavedCards(cards);
        setSelectedCardToken(cards[0]?.id || '');
        setSelectedTransaction(transaction);
        setShowPayModal(true);
    };

    const closePayModal = () => {
        if (isPaying) return;
        setShowPayModal(false);
        setSelectedTransaction(null);
        setSavedCards([]);
        setSelectedCardToken('');
    };

    const handlePayPendingTransaction = async () => {
        if (!selectedCardToken) {
            Swal.fire({ title: 'Card Required', text: 'Please select a saved card to continue.', icon: 'warning', confirmButtonText: 'OK' });
            return;
        }

        if (!selectedTransaction?.id) {
            Swal.fire({ title: 'Invalid Transaction', text: 'Transaction id is missing.', icon: 'error', confirmButtonText: 'OK' });
            return;
        }

        setIsPaying(true);
        try {
            const res = await apiHandler(GRADE_TRANSACTION_PAY, 'POST', {
                token: selectedCardToken,
                grade_transaction_id: selectedTransaction.id,
            });

            if (res?.data?.response?.status === 200) {
                Swal.fire({ title: 'Payment Successful', text: res?.data?.data?.message || 'Transaction paid successfully.', icon: 'success', confirmButtonText: 'OK' });
                closePayModal();
                getGradeTransactionsApi();
            } else {
                Swal.fire({ title: 'Payment Failed', text: res?.data?.response?.message || 'Unable to process payment right now.', icon: 'error', confirmButtonText: 'OK' });
            }
        } catch (error) {
            Swal.fire({
                title: 'Payment Failed',
                text: error?.response?.data?.response?.message || 'Something went wrong while paying this transaction.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setIsPaying(false);
        }
    };

    async function getGradeTransactionsApi() {
        setShowMoreDataLoader(true)
        try {
            const res = await apiHandler(GRADE_TRANSACTIONS, "POST", {
                page: 1,
                limit: 100,
            })
            if (res.data?.response?.status === 200) {
                setGradeTransactions((res?.data?.data?.grade?.data || []).sort((left, right) => new Date(right?.created_at || right?.payment_date || 0) - new Date(left?.created_at || left?.payment_date || 0)))
            } else {
                //navigate('/auth/sign-in')
            }
        } catch (error) {
            console.error(error);
        }
        finally {
            setShowMoreDataLoader(false)
        }
    }

    async function getClassTransactionsApi() {
        setShowMoreDataLoader(true)
        try {
            const res = await apiHandler(CLASS_TRANSACTIONS, "POST", {
                page: 1,
                limit: 100,
            })
            if (res.data?.response?.status === 200) {
                setClassTransactions(res?.data?.data?.classe_transactions?.data || [])
            }
        } catch (error) {
            console.error(error);
        } finally {
            setShowMoreDataLoader(false)
        }
    }

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const res = await apiHandler(GRADE_TRANSACTIONS, "POST", {
                page: 1,
                limit: 100,
            })
            if (res.data?.response?.status === 200) {
                setGradeTransactions((res?.data?.data?.grade?.data || []).sort((left, right) => new Date(right?.created_at || right?.payment_date || 0) - new Date(left?.created_at || left?.payment_date || 0)))
            } else {
                setGradeTransactions([])
            }
        } catch (error) {
            console.error(error);
        } finally {
            setRefreshing(false)
        }
    }

    return (
        <>
            <ProfileHeader title="My Transactions" img={img6} />
            <div id="content-page" className="content-page">
                <Container>
                    <Tab.Container id="left-tabs-example" defaultActiveKey="first">
                        <Card className="p-0 mb-4">
                            <Card.Body className="p-0">
                                <div className="user-tabing">
                                    <Nav as="ul" variant="pills" className="d-flex align-items-center justify-content-center profile-feed-items p-0 m-0">
                                        <Nav.Item as="li" className="col-12 col-sm-6 p-0">
                                            <Nav.Link href="#grade-tab" eventKey="first" role="button" className="text-center p-3">Grade Transactions</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item as="li" className="col-12 col-sm-6 p-0">
                                            <Nav.Link href="#class-tab" eventKey="second" role="button" className="text-center p-3">Class Transactions</Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </div>
                            </Card.Body>
                        </Card>

                        <Tab.Content>
                            <Tab.Pane eventKey="first">
                                <Row className="g-4">
                        <Col lg={12}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-4">
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                        <div>
                                            <h4 className="mb-1">Grade Transactions</h4>
                                            <p className="text-muted mb-0">Review your tuition payments, subsidy deductions, and status updates.</p>
                                        </div>
                                        <Button variant="outline-primary" onClick={handleRefresh} disabled={refreshing || showMoreDataLoader}>
                                            {refreshing ? 'Refreshing...' : 'Refresh'}
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={3} md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <div className="text-muted mb-2">Total Transactions</div>
                                    <h3 className="mb-0">{summary.totalTransactions}</h3>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={3} md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <div className="text-muted mb-2">Total Paid</div>
                                    <h3 className="mb-0">{formatCurrency(summary.totalPaid)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={3} md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <div className="text-muted mb-2">Total Subsidy</div>
                                    <h3 className="mb-0">{formatCurrency(summary.totalSubsidy)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={3} md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <div className="text-muted mb-2">Pending</div>
                                    <h3 className="mb-0">{summary.pendingCount}</h3>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col sm={12}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 bg-white border-0 pt-4 px-4 pb-0">
                                    <div className="header-title">
                                        <h5 className="card-title mb-1">Transaction History</h5>
                                        <p className="text-muted mb-0">Latest transactions appear first.</p>
                                    </div>
                                    <Badge bg="primary" pill>{gradeTransactions.length} Records</Badge>
                                </Card.Header>
                                <Card.Body className="px-4 pb-4 pt-3">
                                    {showMoreDataLoader ? (
                                        <div className="d-flex justify-content-center align-items-center py-5">
                                            <Spinner animation="border" variant="primary" />
                                        </div>
                                    ) : gradeTransactions?.length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table align-middle mb-0">
                                                <thead>
                                                    <tr className="text-muted">
                                                        <th>Grade</th>
                                                        <th>Billing Date</th>
                                                        <th>Payment Method</th>
                                                        <th>Price</th>
                                                        <th>Subsidy</th>
                                                        <th>Total</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {gradeTransactions.map((transaction) => {
                                                        const statusMeta = getStatusMeta(transaction);
                                                        const canPay = isUnpaidTransaction(transaction);
                                                        return (
                                                            <tr key={transaction?.id}>
                                                                <td>
                                                                    <div className="fw-semibold">{transaction?.user_grade?.grade_name?.name || 'Grade'}</div>
                                                                    <small className="text-muted">{transaction?.user_grade?.grade_name?.slug || '-'}</small>
                                                                </td>
                                                                <td>{Moment(transaction?.payment_date || transaction?.created_at).format('MMM D, YYYY')}</td>
                                                                <td>{getPaymentTypeLabel(transaction?.user_grade?.grade_name?.payment_type)}</td>
                                                                <td>{formatCurrency(transaction?.price)}</td>
                                                                <td>{formatCurrency(transaction?.subsidy_amount)}</td>
                                                                <td className="fw-semibold">{formatCurrency(transaction?.total_amount)}</td>
                                                                <td>
                                                                    <Badge bg={statusMeta.variant} className="d-inline-flex align-items-center gap-1">
                                                                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{statusMeta.icon}</span>
                                                                        {statusMeta.label}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    {canPay ? (
                                                                        <Button
                                                                            size="sm"
                                                                            variant={isSelectedPayRow(transaction) ? 'primary' : 'outline-primary'}
                                                                            onClick={() => openPayModal(transaction)}
                                                                            style={isSelectedPayRow(transaction) ? { fontWeight: 600 } : undefined}
                                                                        >
                                                                            Pay Now
                                                                        </Button>
                                                                    ) : (
                                                                        <span className="text-muted">-</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <NoDataFound txtData={"Transactions Not Found!"} />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                                </Row>
                            </Tab.Pane>

                            <Tab.Pane eventKey="second">
                                <Row className="g-4">
                                    <Col sm={12}>
                                        {classTransactions?.length > 0 ? (
                                            <Card className="border-0 shadow-sm">
                                                <Card.Header className="d-flex justify-content-between">
                                                    <div className="header-title">
                                                        <h5 className="card-title mb-0">Class Transaction History</h5>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body>
                                                    <div className="table-responsive">
                                                        <table className="table align-middle mb-0">
                                                            <thead>
                                                                <tr className="text-muted">
                                                                    <th>Class</th>
                                                                    <th>Payment Date</th>
                                                                    <th>Payment Method</th>
                                                                    <th>Price</th>
                                                                    <th>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {classTransactions.map((transaction) => (
                                                                    <tr key={transaction?.id}>
                                                                        <td>
                                                                            <Link to={`/dashboards/app/class-detail/${transaction?.class_name?.slug}`} className="text-decoration-none fw-semibold">
                                                                                {transaction?.class_name?.name || 'Class'}
                                                                            </Link>
                                                                        </td>
                                                                        <td>{Moment(transaction?.class_student?.last_payment_date || transaction?.created_at).format('MMM D, YYYY')}</td>
                                                                        <td>{Number(transaction?.class_student?.payment_type) === 0 ? 'Weekly Payment' : Number(transaction?.class_student?.payment_type) === 1 ? 'Monthly Payment' : 'One Time Payment'}</td>
                                                                        <td>{formatCurrency(transaction?.price)}</td>
                                                                        <td>
                                                                            <Badge bg={Number(transaction?.status) === 1 ? 'success' : 'warning'}>
                                                                                {Number(transaction?.status) === 1 ? 'Success' : 'Pending'}
                                                                            </Badge>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        ) : (
                                            <NoDataFound txtData={"Transactions Not Found!"} />
                                        )}
                                    </Col>
                                </Row>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Container>
            </div>

            <Modal show={showPayModal} onHide={closePayModal} centered>
                <Modal.Header closeButton={!isPaying}>
                    <Modal.Title style={{ fontSize: '16px' }}>Pay Pending Transaction</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2 text-muted">Grade: <span className="text-dark fw-semibold">{selectedTransaction?.user_grade?.grade_name?.name || '-'}</span></p>
                    <p className="mb-3 text-muted">Amount: <span className="text-dark fw-semibold">{formatCurrency(selectedTransaction?.total_amount || selectedTransaction?.price)}</span></p>

                    <Form.Group className="mb-3">
                        <Form.Label>Select Saved Card</Form.Label>
                        <Form.Select value={selectedCardToken} onChange={(event) => setSelectedCardToken(event.target.value)} disabled={isPaying}>
                            <option value="">Select card</option>
                            {savedCards.map((card) => (
                                <option key={card.id} value={card.id}>
                                    {card.brand || 'Card'} ending {card.last4 || '****'} ({card.expMonth || '--'}/{card.expYear || '--'})
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={closePayModal} disabled={isPaying}>Cancel</Button>
                    <Button variant="primary" onClick={handlePayPendingTransaction} disabled={isPaying || !selectedCardToken}>
                        {isPaying ? 'Processing...' : 'Pay Now'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )

}

export default MyTransactions;